import React, { useEffect, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Row,
  Table,
  Col,
  Card,
  CardBody,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
  Badge,
} from "reactstrap"

import { useLocation, useNavigate } from "react-router"
import DeleteModal from "components/Common/DeleteModal"
import Paginations from "components/Common/Paginations"
import Filters from "components/Common/Filters"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import {
  deleteAdminUser,
  getAllBranchs,
  getAllRoles,
  getAllUsers,
  updateAdminUserStatus,
} from "store/actions/usersAction"
import Switch from "components/Shared/Switch"
import isPermissionEnabled from "common/isPermissionEnabled"
import { showAlert } from "store/reducers/alertReducer"
import { commonItemPerPage, pageSizes } from "components/Common/constants"
import DownloadCSV from "components/Shared/DownloadCSV"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import TableShimmer from "components/Shared/TableShimmer"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import { useTranslation } from "react-i18next"

const Users = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Users List | Namq")

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const rolesParam = searchParams.get("roles")
  const locationsParam = searchParams.get("locations")

  const dispatch = useDispatch()
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const navigate = useNavigate()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "2"),
    roles: rolesParam
      ? rolesParam.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    locations: locationsParam
      ? locationsParam.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    sortOrder: parseInt(searchParams.get("sortOrder") || "1"),
  })
  const {
    loading,
    isLoading,
    isUpdating,
    totalCount,
    totalPages,
    currentPage,
    usersList,
    branchsList,
    rolesList,
  } = useSelector(state => state.Users)

  const [itemsPerPage, setItemsPerPage] = useState(commonItemPerPage)
  const [isApiCalled, setIsApiCalled] = useState(false)

  const { userRole, accountType } = useSelector(state => state.Login)
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    create: null,
    edit: null,
    delete: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      6,
      "usersPermission_1",
      accountType
    )
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      6,
      "usersPermission_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      6,
      "usersPermission_3",
      accountType
    )
    const isPermissionDelete = isPermissionEnabled(
      userRole,
      6,
      "usersPermission_4",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      create: isPermissionCreate,
      edit: isPermissionEdit,
      delete: isPermissionDelete,
    })
  }, [userRole])

  useEffect(() => {
    fetchUsersData()
  }, [])

  useEffect(() => {
    fetchAllBranchs()
  }, [])

  useEffect(() => {
    getRolesList()
  }, [])

  const getRolesList = () => {
    dispatch(getAllRoles())
  }

  const fetchUsersData = () => {
    const { status, roles, locations, sortOrder } = selectedFilters
    const rolesIds = roles?.map(item => item.value) || []
    const locationsIds = locations?.map(item => item.value) || []

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      status: status, // 0 deactive, 1 active, 2 for all
      orderBy: sortOrder,
      keyword: searchTerm,
      role: rolesIds,
      location: locationsIds,
    }
    dispatch(getAllUsers(payload))
    setIsApiCalled(true)
  }
  const fetchAllBranchs = () => {
    dispatch(getAllBranchs())
  }

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
      status: "2", // 2 FOR DELETE
    }
    dispatch(deleteAdminUser({ ...payload, setShowDeleteModal }))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleEnableUser = (id, status) => {
    setSelectedId(id)
    const payload = {
      id,
      status: status === 1 ? "0" : "1",
    }
    dispatch(updateAdminUserStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleFetchUsers = (page = 1) => {
    const { status, roles, locations, sortOrder } = selectedFilters
    const rolesIds = roles?.map(item => item.value) || []
    const locationsIds = locations?.map(item => item.value) || []

    const searchParams = new URLSearchParams()
    searchTerm && searchParams.set("search", searchTerm)
    searchParams.set("status", status.toString())
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())

    if (roles.length > 0) {
      const rolesString = roles
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("roles", rolesString)
    }

    if (locations.length > 0) {
      const locationsString = locations
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("locations", locationsString)
    }

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status), // 0 deactive, 1 active, 2 for all
      orderBy: Number(sortOrder),
      role: rolesIds,
      keyword: searchTerm || "",
      location: locationsIds,
    }

    dispatch(getAllUsers(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/users?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    handleFetchUsers(1) // Always search from page 1
  }

  const onPageChange = page => {
    handleFetchUsers(page)
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }
  const filters = [
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All"), value: 2 },
        { label: t("Active"), value: 1 },
        { label: t("Deactive"), value: 0 },
      ],
      placeholder: t("All"),
    },
    {
      name: "roles",
      type: "multiSelect",
      options: [
        // { label: "All", value: [] },
        ...rolesList.map(role => ({
          label: language === "sa" ? role.nameAr || "-" : role.name,
          value: role._id,
        })),
      ],
      placeholder: t("Select Roles"),
    },
    {
      name: "locations",
      type: "multiSelect",
      options: [
        // { label: "All", value: [] },
        ...branchsList.map(branch => ({
          label: language === "sa" ? branch.nameAr : branch.name,
          value: branch._id,
        })),
      ],
      placeholder: t("Select Branch"),
    },
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
  ]

  // useEffect(() => {
  //   if (isApiCalled) {
  //     const delayDebounceFn = setTimeout(() => {
  //       handleSearch()
  //     }, 500)

  //     return () => clearTimeout(delayDebounceFn)
  //   }
  // }, [searchTerm])

  // useEffect(() => {
  //   if (isApiCalled) {
  //     handleSearch()
  //   }
  // }, [selectedFilters])

  const handleReset = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      status: 2, // 0 deactive, 1 active, 2 for all
      orderBy: 1,
      keyword: "",
      location: [],
    }
    dispatch(getAllUsers(payload))
    setSelectedFilters({
      status: 2,
      roles: [],
      locations: [],
      sortOrder: 1,
    })
    setSearchTerm("")
    setItemsPerPage(commonItemPerPage)
  }

  const handleExcelDownload = async () => {
    const { status, roles, locations, sortOrder } = selectedFilters
    const rolesIds = roles?.map(item => item.value)

    const payload = {
      status: Number(status),
      keyword: searchTerm || "",
      type: "USER",
      exportType: "xlsx",
      role: rolesIds,
      role: [],
      location: [],
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      DownloadXLSX(xlsxData, t("Users"))
    }
  }

  const exportToCSV = async () => {
    const { status, roles, locations, sortOrder } = selectedFilters
    const rolesIds = roles?.map(item => item.value)

    // DownloadCSV(formattedData, filename)
    const payload = {
      status: Number(status),
      keyword: searchTerm || "",
      type: "USER",
      exportType: "csv",
      role: rolesIds,
      role: [],
      location: [],
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      DownloadCSV(csvData, t("Users"))
    }
  }

  return (
    <>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs />

          <Row>
            <Col xl={12}>
              <Card>
                <DataExportDropdown
                  // onDownloadPDF={handleDownloadPDF}
                  onDownloadCSV={exportToCSV}
                  onDownloadExcel={handleExcelDownload}
                  cardTitle={t("Users List")}
                  isAddBtn={loading ? false : usersPermissions.create}
                  addBtnText={t("Add New User")}
                  onAddBtnClick={() => navigate("/users/add")}
                  isExportData={loading ? false : true}
                />
                <CardBody>
                  {/* {!loading && ( */}
                  <Filters
                    filters={filters}
                    searchTerm={searchTerm}
                    handleSearchChange={handleSearchChange}
                    handleFilterChange={handleFilterChange}
                    handleSearch={handleSearch}
                    onReset={handleReset}
                    selectedFilters={selectedFilters}
                    isCustomPageSize={true}
                    pageSizes={pageSizes}
                    itemsPerPage={itemsPerPage}
                    setItemsPerPage={setItemsPerPage}
                    searchBy={t("Name, Email, Phone Number")}
                    colSize={4}
                  />
                  {/* )} */}

                  <div className="">
                    {loading ? (
                      <TableShimmer row={7} col={5} />
                    ) : usersList.length === 0 ? (
                      <p className="text-center my-2">{t("No users found")}</p>
                    ) : (
                      <Table className="table mb-0" hover>
                        <thead className="table-light full_th">
                          <tr>
                            <th>{t("S. No.")}</th>
                            {/* <th>{t("Username")}</th> */}
                            <th>{t("Name")}</th>
                            <th>{t("Email")}</th>
                            <th>{t("Phone Number")}</th>
                            <th>{t("Role")}</th>
                            <th>{t("Status")}</th>
                            <th>{t("Action")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usersList.map((item, index) => (
                            <tr
                              key={item._id}
                              onClick={() =>
                                navigate(`/users/user-details?id=${item._id}`)
                              }
                              className="cursor-pointer"
                            >
                              <th scope="row">
                                {(currentPage - 1) * 10 + index + 1}
                              </th>
                              {/* <td>{item.username}</td> */}
                              <td>
                                {item.firstName} {item.lastName}
                              </td>
                              <td>{item.email || "-"}</td>
                              <td>{item.phoneNumber || "-"}</td>
                              <td>{item?.role?.name}</td>
                              <td>
                                {item.status === 1 ? (
                                  <Badge className="bg-success">
                                    {t("Active")}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-danger">
                                    {t("Deactive")}
                                  </Badge>
                                )}
                              </td>
                              <td className="d-flex align-items-center gap-2">
                                <div className="gap-1 d-flex">
                                  {usersPermissions.edit && (
                                    <Switch
                                      onClickHandler={e => {
                                        e.stopPropagation()
                                        handleEnableUser(item._id, item.status)
                                      }}
                                      isChecked={item.status === 1}
                                      disabled={
                                        item._id === selectedId && isUpdating
                                      }
                                    />
                                  )}
                                  <UncontrolledDropdown>
                                    <DropdownToggle
                                      tag="a"
                                      className="card-drop"
                                      onClick={e => e.stopPropagation()}
                                    >
                                      <i className="mdi mdi-dots-vertical font-size-18"></i>
                                    </DropdownToggle>
                                    <DropdownMenu className="dropdown-menu-end">
                                      {usersPermissions.edit && (
                                        <DropdownItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            navigate(
                                              `/users/edit?id=${item._id}`
                                            )
                                          }}
                                        >
                                          <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                                          {t("Edit")}
                                        </DropdownItem>
                                      )}
                                      <DropdownItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          navigate(
                                            `/users/user-details?id=${item._id}`
                                          )
                                        }}
                                      >
                                        <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
                                        {t("View")}
                                      </DropdownItem>

                                      {usersPermissions.delete && (
                                        <DropdownItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            setItemIdToDelete(item._id)
                                            setShowDeleteModal(true)
                                          }}
                                        >
                                          <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                                          {t("Delete")}
                                        </DropdownItem>
                                      )}
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                  {!loading && usersList.length > 0 && (
                    <div className="mt-3">
                      <Paginations
                        data={usersList}
                        itemsPerPage={itemsPerPage}
                        onPageChange={onPageChange}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowDeleteModal(false)}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("User"),
        })}
      />
    </>
  )
}

export default Users
